import React, { useEffect, useState } from 'react';
import { Save, Upload, Loader2, KeyRound } from 'lucide-react';
import { portalApi, usePortalAuth, formatApiError } from '../../contexts/PortalAuthContext';
import { toast } from 'sonner';

export default function PortalProfile() {
  const { user, refresh } = usePortalAuth();
  const [form, setForm] = useState({
    full_name: '', bio: '', location: '', business: '', avatar_url: '',
    social_links: { twitter: '', instagram: '', website: '', linkedin: '' },
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  // Password change
  const [pwOpen, setPwOpen] = useState(false);
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [pwSaving, setPwSaving] = useState(false);

  useEffect(() => {
    if (user) {
      setForm({
        full_name: user.full_name || '',
        bio: user.bio || '',
        location: user.location || '',
        business: user.business || '',
        avatar_url: user.avatar_url || '',
        social_links: { twitter: '', instagram: '', website: '', linkedin: '', ...(user.social_links || {}) },
      });
    }
  }, [user]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await portalApi.put('/profile', form);
      await refresh();
      toast.success('Profile saved');
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const onAvatar = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const { data } = await portalApi.post('/profile/avatar', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setForm(f => ({ ...f, avatar_url: data.avatar_url }));
      await refresh();
      toast.success('Avatar updated');
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading(false);
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    setPwSaving(true);
    try {
      await portalApi.post('/auth/change-password', {
        current_password: currentPw,
        new_password: newPw,
      });
      toast.success('Password changed');
      setCurrentPw(''); setNewPw(''); setPwOpen(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPwSaving(false);
    }
  };

  return (
    <div data-testid="portal-profile">
      <header className="mb-8">
        <div className="text-amber-400 text-xs uppercase tracking-widest mb-2">Account</div>
        <h1 className="text-3xl font-semibold text-white">My Profile</h1>
        <p className="text-slate-400 mt-2">Tell your cohort who you are. This appears on the Cohort page.</p>
      </header>

      <form onSubmit={save} className="space-y-6">
        <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-6">
          <div className="flex flex-col sm:flex-row items-start gap-6">
            <div className="flex flex-col items-center gap-3">
              {form.avatar_url ? (
                <img
                  src={form.avatar_url.startsWith('http') ? form.avatar_url : `${process.env.REACT_APP_BACKEND_URL}${form.avatar_url}`}
                  alt=""
                  className="w-28 h-28 rounded-full object-cover border-2 border-slate-700"
                />
              ) : (
                <div className="w-28 h-28 rounded-full bg-slate-800 flex items-center justify-center text-3xl font-semibold text-slate-400">
                  {(form.full_name || 'U').slice(0,1).toUpperCase()}
                </div>
              )}
              <label className="text-xs cursor-pointer text-amber-400 hover:underline flex items-center gap-1.5">
                {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                {uploading ? 'Uploading…' : 'Change avatar'}
                <input type="file" accept="image/*" className="hidden" onChange={onAvatar} data-testid="avatar-upload-input" />
              </label>
            </div>

            <div className="flex-1 w-full grid sm:grid-cols-2 gap-4">
              <Field label="Full name" value={form.full_name} onChange={v => setForm({...form, full_name: v})} testId="profile-full-name" />
              <Field label="Business / brand" value={form.business} onChange={v => setForm({...form, business: v})} testId="profile-business" />
              <Field label="Location (city, country)" value={form.location} onChange={v => setForm({...form, location: v})} testId="profile-location" />
              <Field label="Email (read-only)" value={user?.email || ''} disabled />
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-6">
          <label className="block text-xs font-medium text-slate-300 mb-1.5">Short bio</label>
          <textarea
            value={form.bio}
            onChange={e => setForm({...form, bio: e.target.value})}
            rows={4}
            placeholder="Who you are, what you're building, why you joined…"
            className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:border-amber-500 outline-none resize-y"
            data-testid="profile-bio"
          />
        </div>

        <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-6">
          <h2 className="text-sm font-semibold text-white mb-4">Social & links</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Website" placeholder="https://" value={form.social_links.website}
              onChange={v => setForm({...form, social_links: {...form.social_links, website: v}})} testId="profile-website" />
            <Field label="Instagram" placeholder="@handle or url" value={form.social_links.instagram}
              onChange={v => setForm({...form, social_links: {...form.social_links, instagram: v}})} testId="profile-instagram" />
            <Field label="X / Twitter" placeholder="@handle or url" value={form.social_links.twitter}
              onChange={v => setForm({...form, social_links: {...form.social_links, twitter: v}})} testId="profile-twitter" />
            <Field label="LinkedIn" placeholder="url" value={form.social_links.linkedin}
              onChange={v => setForm({...form, social_links: {...form.social_links, linkedin: v}})} testId="profile-linkedin" />
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold rounded-lg px-5 py-2.5 disabled:opacity-60"
            data-testid="profile-save-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save profile
          </button>
          <button
            type="button"
            onClick={() => setPwOpen(o => !o)}
            className="inline-flex items-center gap-2 text-slate-300 hover:text-white text-sm"
            data-testid="change-pw-toggle"
          >
            <KeyRound className="w-4 h-4" /> Change password
          </button>
        </div>
      </form>

      {pwOpen && (
        <form onSubmit={changePassword} className="mt-6 rounded-xl border border-slate-800 bg-slate-900/60 p-6 max-w-md space-y-3" data-testid="change-pw-form">
          <Field label="Current password" type="password" value={currentPw} onChange={setCurrentPw} testId="current-pw" />
          <Field label="New password (min 8 chars)" type="password" value={newPw} onChange={setNewPw} testId="new-pw" />
          <button
            type="submit"
            disabled={pwSaving}
            className="inline-flex items-center gap-2 bg-slate-700 hover:bg-slate-600 text-white text-sm rounded-lg px-4 py-2 disabled:opacity-60"
            data-testid="change-pw-submit"
          >
            {pwSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            Update password
          </button>
        </form>
      )}
    </div>
  );
}

function Field({ label, value, onChange, disabled, type='text', placeholder, testId }) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-300 mb-1.5">{label}</label>
      <input
        type={type}
        value={value || ''}
        disabled={disabled}
        placeholder={placeholder}
        onChange={onChange ? (e) => onChange(e.target.value) : undefined}
        readOnly={!onChange || disabled}
        className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:border-amber-500 outline-none disabled:opacity-60"
        data-testid={testId}
      />
    </div>
  );
}
